import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTimePicker, renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers';
import { ConversationDraftSendOptionSection } from '@provision/conversation-types';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DynamicIcon } from 'src/components';
import { TimeFormats } from 'src/graphql';
import { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { useArrowNavigation, useDisplayTime } from 'src/utils/hooks';
import {
  ensureFutureDate,
  getDateFromNaturalLanguage,
  resolveNaturalLanguageDate,
} from 'src/utils/natural-language-date';
import {
  ScheduleListItem,
  useScheduleSendModalState,
} from './hooks/use-schedule-send-modal-state';
import { SendOptionItem } from './send-options-button';

interface ScheduleSendModalProps {
  open: boolean;
  initialDate?: string | null;
  initialConditions?: DraftScheduleSendUnless[] | null;
  sendOptions: SendOptionItem[];
  onClose: () => void;
  onSubmit: (params: { scheduledAt: string; sendUnless: DraftScheduleSendUnless[] }) => void;
  onSelectOption: (optionId: string) => Promise<void> | void;
  isProcessingOption?: boolean;
}

const NATURAL_LANGUAGE_DAY_START = '9:00am';

const SNOOZE_UNIT_MAP: Record<string, dayjs.ManipulateType> = {
  minute: 'minute',
  minutes: 'minute',
  min: 'minute',
  mins: 'minute',
  m: 'minute',
  hour: 'hour',
  hours: 'hour',
  hr: 'hour',
  hrs: 'hour',
  h: 'hour',
  day: 'day',
  days: 'day',
  week: 'week',
  weeks: 'week',
  month: 'month',
  months: 'month',
};

const SNOOZE_KEYWORD_PRESETS: {
  keywords: string[];
  amount: number;
  unit: dayjs.ManipulateType;
}[] = [
  { keywords: ['tomorrow', 'tomorrow morning'], amount: 1, unit: 'day' },
  { keywords: ['next day'], amount: 1, unit: 'day' },
  { keywords: ['next week'], amount: 1, unit: 'week' },
  { keywords: ['next month'], amount: 1, unit: 'month' },
];

type SnoozeCommandDefinition =
  | { type: 'offset'; amount: number; unit: dayjs.ManipulateType }
  | { type: 'absolute'; iso: string };

const formatSnoozeDuration = (amount: number, unit: dayjs.ManipulateType): string => {
  const unitLabel = amount === 1 ? unit : `${unit}s`;
  return `${amount} ${unitLabel}`;
};

const resolveFutureIso = (input: string): string | null => {
  const parsedWithDayStart = getDateFromNaturalLanguage(input, {
    dayStart: NATURAL_LANGUAGE_DAY_START,
  });
  if (!parsedWithDayStart) {
    return null;
  }
  const parsedWithoutDayStart = getDateFromNaturalLanguage(input);
  const resolved = resolveNaturalLanguageDate(
    parsedWithDayStart,
    parsedWithoutDayStart || parsedWithDayStart,
  );
  const ensured = ensureFutureDate(resolved, input);
  if (!ensured) {
    return null;
  }
  const parsedDate = dayjs(ensured);
  return parsedDate.isValid() ? parsedDate.toISOString() : null;
};

const parseSnoozeCommand = (value: string): SnoozeCommandDefinition | null => {
  const normalized = value.trim().toLowerCase();
  if (!normalized?.includes('snooze')) {
    return null;
  }

  const amountMatch =
    /snooze(?:\s+(?:for|in|after))?\s+(?<amount>a|an|\d+)\s+(?<unit>minutes?|mins?|min|hours?|hrs?|hr|days?|weeks?|months?)/.exec(
      normalized,
    );
  if (amountMatch?.groups) {
    const amountRaw = amountMatch.groups.amount;
    const resolvedAmount =
      amountRaw === 'a' || amountRaw === 'an' ? 1 : Number.parseInt(amountRaw, 10);
    if (Number.isFinite(resolvedAmount) && resolvedAmount > 0) {
      const unitKey = amountMatch.groups.unit;
      const resolvedUnit = SNOOZE_UNIT_MAP[unitKey];
      if (resolvedUnit) {
        return { type: 'offset', amount: resolvedAmount, unit: resolvedUnit };
      }
    }
  }

  for (const preset of SNOOZE_KEYWORD_PRESETS) {
    const hasKeyword = preset.keywords.some(
      (keyword) =>
        normalized.includes(`snooze ${keyword}`) ||
        normalized.includes(`snooze until ${keyword}`) ||
        normalized.includes(`snooze till ${keyword}`),
    );
    if (hasKeyword) {
      return { type: 'offset', amount: preset.amount, unit: preset.unit };
    }
  }

  const snoozeIndex = normalized.indexOf('snooze');
  const remainder = value.slice(snoozeIndex + 'snooze'.length).trim();
  if (remainder) {
    const iso = resolveFutureIso(remainder);
    if (iso) {
      return { type: 'absolute', iso };
    }
  }

  return null;
};

const buildSnoozeCommand = (
  command: SnoozeCommandDefinition,
  displayTime: (value: dayjs.Dayjs | string | Date) => string,
) => {
  if (command.type === 'offset') {
    const target = dayjs()
      .add(command.amount, command.unit)
      .set('hour', 9)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    return {
      id: `send-and-snooze:${command.amount}:${command.unit}`,
      label: `Send and snooze for ${formatSnoozeDuration(command.amount, command.unit)}`,
      description: `Snooze until ${target.format('ddd, MMM D')} at ${displayTime(target)}`,
      icon: 'alarm-clock',
    } as const;
  }

  const target = dayjs(command.iso);
  const datePart = target.format('ddd, MMM D');
  const timePart = displayTime(target);
  return {
    id: `send-and-snooze-until:${encodeURIComponent(command.iso)}`,
    label: `Send and snooze until ${datePart}`,
    description: `Snooze until ${datePart} at ${timePart}`,
    icon: 'alarm-clock',
  } as const;
};

export const ScheduleSendModal = ({
  open,
  initialDate,
  initialConditions,
  sendOptions: _sendOptions,
  onClose,
  onSubmit,
  onSelectOption: _onSelectOption,
  isProcessingOption = false,
}: ScheduleSendModalProps): React.JSX.Element => {
  // Track if we opened in reschedule mode (affects initial step only)
  const isRescheduling = Boolean(initialDate);
  const {
    inputValue,
    placeholder,
    listItems,
    hasNoResults,
    selectedDateLabel,
    customDate,
    isPickerOpen,
    isValid,
    options,
    toggleCondition,
    minutesStep,
    onInputChange,
    openPicker,
    closePicker,
    setCustomDate,
    commitCustomDate,
    buildSubmitPayload,
  } = useScheduleSendModalState({ open, initialDate, initialConditions });

  const { timeFormatPref, displayTime } = useDisplayTime();
  const theme = useTheme();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const rulesChipsContainerRef = useRef<HTMLDivElement | null>(null);
  const onHandleKeyDown = useArrowNavigation();
  const [hasScheduleSelection, setHasScheduleSelection] = useState<boolean>(
    Boolean(initialDate),
  );
  // Rules are displayed inline in Step 2 (no popover)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setHasScheduleSelection(Boolean(initialDate));
      return;
    }
    setHasScheduleSelection(false);
  }, [initialDate, open]);

  // Ensure the input is focused when Step 1 is active
  const focusInput = useCallback(() => {
    if (!hasScheduleSelection && inputRef.current) {
      inputRef.current.focus();
      try {
        inputRef.current.select();
      } catch {
        // ignore if select not available
      }
    }
  }, [hasScheduleSelection]);

  useEffect(() => {
    let id = 0;
    if (open && !hasScheduleSelection) {
      // Delay to let Dialog focus trap settle before focusing input
      id = window.setTimeout(() => focusInput(), 0);
    }

    return () => (id ? window.clearTimeout(id) : undefined);
  }, [open, hasScheduleSelection, focusInput]);

  const sendAndOptionsList = useMemo(
    () =>
      _sendOptions.filter(
        (option) => option.section === ConversationDraftSendOptionSection.SendAnd,
      ),
    [_sendOptions],
  );

  const dynamicSnoozeCommand = useMemo(() => {
    if (!inputValue.trim()) {
      return null;
    }
    const parsed = parseSnoozeCommand(inputValue);
    if (!parsed) return null;
    return buildSnoozeCommand(parsed, displayTime);
  }, [displayTime, inputValue]);

  const handleSendAndSelect = useCallback(
    (optionId: string): void => {
      void _onSelectOption(optionId);
      onInputChange('');
      onClose();
    },
    [_onSelectOption, onClose, onInputChange],
  );

  const sendAndCommands = useMemo(() => {
    if (isRescheduling) {
      return [];
    }

    const wantsActions = /\b(send|solve|snooze)\b/i.test(inputValue);
    const isSearching = Boolean(inputValue.trim());
    const baseAll = sendAndOptionsList.map((option) => ({
      id: option.id,
      label: option.label,
      description: option.description ?? '',
      onSelect: () => handleSendAndSelect(option.id),
    }));

    // Only include these by default when there's no specific intent typed
    const defaultIds = new Set(['send-and-solve', 'send-and-snooze-tomorrow']);
    const baseDefault = baseAll.filter((item) => defaultIds.has(item.id));

    const withDynamic: typeof baseAll = [];
    if (dynamicSnoozeCommand && !baseAll.some((item) => item.id === dynamicSnoozeCommand.id)) {
      withDynamic.push({
        id: dynamicSnoozeCommand.id,
        label: dynamicSnoozeCommand.label,
        description: dynamicSnoozeCommand.description,
        onSelect: () => handleSendAndSelect(dynamicSnoozeCommand.id),
      });
    }

    // If user is searching but not for send/solve/snooze, hide send-and options
    if (isSearching && !wantsActions) {
      return [];
    }

    return wantsActions ? [...withDynamic, ...baseAll] : [...withDynamic, ...baseDefault];
  }, [
    dynamicSnoozeCommand,
    handleSendAndSelect,
    sendAndOptionsList,
    inputValue,
    isRescheduling,
  ]);

  // No text input in simplified flow

  const handleScheduleItemSelect = useCallback(
    (item: ScheduleListItem) => {
      if (isProcessingOption) {
        return;
      }

      item.onSelect();
      // Only proceed to Step 2 for non-custom selections; for custom wait for picker accept
      if (item.id !== 'custom') {
        setHasScheduleSelection(true);
      }
    },
    [isProcessingOption],
  );

  const handlePickerAccept = useCallback(() => {
    commitCustomDate();
    setHasScheduleSelection(true);
  }, [commitCustomDate]);

  const hasListItems = listItems.length > 0;
  const hasActionItems = !isRescheduling && sendAndCommands.length > 0;

  const handleSave = useCallback(() => {
    const payload = buildSubmitPayload();
    if (!payload) {
      return;
    }

    try {
      onSubmit(payload);
    } catch (error) {
      // safe to ignore
      console.error(error);
    }

    onClose();
  }, [buildSubmitPayload, onClose, onSubmit]);

  const selectedOptions = useMemo(() => options.filter((o) => o.selected), [options]);

  const handleClearRules = useCallback(() => {
    options.filter((o) => o.selected).forEach((o) => toggleCondition(o.type));
  }, [options, toggleCondition]);

  const goToStep1 = useCallback(() => setHasScheduleSelection(false), []);

  const goToStep2 = useCallback(() => setHasScheduleSelection(true), []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {hasScheduleSelection ? (
            <IconButton aria-label="Go back" size="small" onClick={goToStep1}>
              <DynamicIcon name="arrow-left" library="lucide" />
            </IconButton>
          ) : null}
          <Typography variant="overline" color="text.secondary">
            Smart send
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent
        ref={dialogRef}
        onKeyDown={(event) => {
          onHandleKeyDown(event, dialogRef);
          // From text input, Tab should focus first list option in Step 1
          if (
            event.key === 'Tab' &&
            !event.shiftKey &&
            !hasScheduleSelection &&
            document.activeElement?.tagName.toLowerCase() === 'input'
          ) {
            const firstOption = listRef.current?.querySelector<HTMLElement>(
              '.MuiListItemButton-root',
            );
            if (firstOption) {
              event.preventDefault();
              firstOption.focus();
              return;
            }
          }
          if (event.key === 'Enter') {
            const target = event.target as HTMLElement | null;
            if (target && rulesChipsContainerRef.current?.contains(target)) {
              // Let chips handle Enter for toggling
              return;
            }
            // Trigger primary action with Enter
            event.preventDefault();
            if (!hasScheduleSelection) {
              if (isValid && !isProcessingOption) {
                goToStep2();
              }
            } else {
              if (isValid && !isProcessingOption && isOnline) {
                handleSave();
              }
            }
          }
        }}
        sx={{ pt: 1 }}
      >
        {/* Step 1: Choose time */}
        {!hasScheduleSelection ? (
          <Stack spacing={1}>
            <TextField
              autoFocus
              fullWidth
              placeholder={placeholder}
              variant="standard"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              inputRef={inputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DynamicIcon
                      name="clock"
                      library="lucide"
                      sx={{ width: '1.5rem', height: '1.5rem' }}
                    />
                  </InputAdornment>
                ),
              }}
              disabled={isProcessingOption}
            />
            <List ref={listRef}>
              {hasActionItems
                ? sendAndCommands.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={item.onSelect}
                      disabled={isProcessingOption}
                    >
                      <ListItemText primary={item.label} secondary={item.description} />
                    </ListItemButton>
                  ))
                : null}

              {hasListItems
                ? listItems.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleScheduleItemSelect(item)}
                      disabled={isProcessingOption}
                    >
                      <ListItemText
                        primary={item.primary}
                        sx={{
                          width: 'min-content',
                          '&::first-letter': { textTransform: 'capitalize' },
                        }}
                      />
                      <ListItemText
                        secondary={item.secondary}
                        sx={{ textAlign: 'end', width: 'min-content' }}
                      />
                    </ListItemButton>
                  ))
                : null}

              <DateTimePicker
                open={isPickerOpen}
                onOpen={openPicker}
                onClose={closePicker}
                value={customDate}
                onChange={setCustomDate}
                onAccept={handlePickerAccept}
                disablePast
                desktopModeMediaQuery="(min-width: 99999px)"
                minutesStep={minutesStep}
                views={['day', 'hours', 'minutes']}
                viewRenderers={{
                  hours: renderMultiSectionDigitalClockTimeView,
                  minutes: renderMultiSectionDigitalClockTimeView,
                }}
                ampm={timeFormatPref === TimeFormats.TwelveHour}
                slotProps={{ textField: { sx: { display: 'none' } } }}
                sx={{ visibility: 'hidden', position: 'absolute', bottom: 0, opacity: 0 }}
              />
            </List>

            {!hasListItems && !hasActionItems && inputValue.trim() ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                {hasNoResults ? 'No matching time recognized' : ''}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {/* Step 2: Confirm */}
        {hasScheduleSelection ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedDateLabel}
              </Typography>
              <IconButton size="small" aria-label="Edit scheduled time" onClick={goToStep1}>
                <DynamicIcon name="pencil" library="lucide" size={18} />
              </IconButton>
            </Stack>

            {/* Rules toggles: compact chips to avoid layout shift */}
            <Box ref={rulesChipsContainerRef}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }} minWidth="70px">
                  Cancel if:
                </Typography>

                <Box display="flex" flexDirection="row" gap="0.5rem" flexWrap="wrap">
                  {options.map((option) => {
                    const selected = option.selected;
                    const commonProps = {
                      clickable: true,
                      onClick: () => toggleCondition(option.type),
                      size: 'small' as const,
                      label: option.label,
                      'aria-label': `Toggle rule: ${option.label}`,
                      'aria-pressed': selected,
                    };
                    return selected ? (
                      <Chip
                        {...commonProps}
                        icon={<DynamicIcon name="check" library="lucide" size={18} />}
                        color="primary"
                        variant="filled"
                        sx={{
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    ) : (
                      <Chip
                        {...commonProps}
                        icon={<DynamicIcon name="plus" library="lucide" size={18} />}
                        variant="outlined"
                        color="default"
                        sx={{
                          borderColor: alpha(theme.palette.text.primary, 0.28),
                          color: theme.palette.text.primary,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.06),
                          },
                        }}
                      />
                    );
                  })}
                </Box>

                <Button
                  onClick={handleClearRules}
                  size="small"
                  color="inherit"
                  sx={{ height: '20px' }}
                  disabled={!selectedOptions.length}
                >
                  Clear
                </Button>
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>

      {/* Footer actions with a single divider above */}
      {!hasScheduleSelection ? (
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          }}
        >
          <Button onClick={onClose} disabled={isProcessingOption}>
            Cancel
          </Button>
          <Button
            onClick={goToStep2}
            disabled={!isValid || isProcessingOption}
            variant="contained"
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      ) : (
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {!isValid ? (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                Selected time has already passed. Edit the time above.
              </Typography>
            ) : null}
            {!isOnline ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You appear to be offline. Scheduling is disabled until connectivity is
                restored.
              </Typography>
            ) : null}
          </Box>
          <Button
            onClick={handleSave}
            disabled={!isValid || isProcessingOption || !isOnline}
            variant="contained"
            autoFocus
          >
            Schedule
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ScheduleSendModal;
